import { Injectable, inject, effect, OnDestroy } from '@angular/core';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';
import { db } from './firebase.init';

@Injectable({
  providedIn: 'root'
})
export class PresenceService implements OnDestroy {
  private authService = inject(AuthService);
  private configService = inject(ConfigService);

  private heartbeatInterval?: any;
  private visibilityHandler?: () => void;
  private lastPingTime = 0;
  private currentActiveUid: string | null = null;

  constructor() {
    effect(() => {
      const user = this.authService.user();
      const isAdmin = this.authService.isAdmin();
      const isTrackingEnabled = this.configService.config()?.track_user_presence ?? false;

      // Only track if user is logged in, NOT an admin, and tracking is enabled
      if (user && !isAdmin && isTrackingEnabled) {
        if (this.currentActiveUid !== user.uid) {
          this.startHeartbeat(user.uid);
        }
      } else {
        this.stopHeartbeat();
      }
    });
  }

  private startHeartbeat(uid: string) {
    this.stopHeartbeat();
    this.currentActiveUid = uid;

    // Send immediate initial ping
    this.ping(uid);

    // Setup 90-second interval
    if (typeof window !== 'undefined') {
      this.heartbeatInterval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.ping(uid);
        }
      }, 90000);

      // Also ping when returning to the tab if more than 60s has elapsed
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible' && Date.now() - this.lastPingTime > 60000) {
          this.ping(uid);
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  private async ping(uid: string) {
    this.lastPingTime = Date.now();
    try {
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, {
        last_active_at: serverTimestamp()
      });
    } catch (err) {
      console.warn('Presence heartbeat error:', err);
    }
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
    this.currentActiveUid = null;
  }

  ngOnDestroy() {
    this.stopHeartbeat();
  }
}
